import { useQuery } from "@tanstack/react-query";
import { TrainIdentifier } from "@vygruppen/vy-train-map";
import { getBackendUrl } from "api/common";
import { queryFnPOST } from "api/tanStackQuery/helpers";
import { PreviewStatus } from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/infrastructureEventModal/Preview";
import { PreviewResponseBatch } from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/utils";
import {
  TrainFormGroup,
  trainFormGroup,
} from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/TrainInfoModal/types/formSchema";
import { TrainInfoRequest } from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/TrainInfoModal/types/requestSchema";
import { singleTrainFormToRequestBody } from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/TrainInfoModal/types/transformers";

type PreviewProps = {
  formData: TrainFormGroup[];
};

export type PreviewTrainRequest = {
  info: TrainInfoRequest | null;
  train: TrainIdentifier;
};

type Body = PreviewTrainRequest[];

const QUERY_KEY = "OPERATIONAL_INFO_BATCH_PREVIEW";

export const useTrainInfoBatchPreviews = ({ formData }: PreviewProps) => {
  const parsedForms = formData.map((trainGroup) =>
    trainFormGroup.safeParse(trainGroup),
  );

  const parsedFormsSuccessful = parsedForms.filter(
    (parsedForm) => parsedForm.success,
  );

  const requestBody = parsedFormsSuccessful.flatMap<Body>((parsedForm) => {
    if (!parsedForm.data) return [];
    return parsedForm.data.trainIdsInGroup.map<PreviewTrainRequest>(
      (trainId) => {
        const info = singleTrainFormToRequestBody(
          parsedForm.data.trainForm,
          trainId,
        );

        return {
          train: trainId,
          info,
        };
      },
    );
  });

  const enabled = parsedFormsSuccessful.length > 0;
  const { status: _previewStatus, data: previewData } = useQuery({
    queryKey: [
      QUERY_KEY,
      ...parsedForms.flatMap((t) => t.data?.trainIdsInGroup),
      ...parsedForms.map((t) => t.data?.trainForm),
    ],
    refetchOnWindowFocus: false,
    queryFn: ({ signal }) =>
      queryFnPOST<PreviewResponseBatch>({
        url: `${getBackendUrl()}/trainInformation/preview`,
        body: requestBody,
        signal,
      }),
    enabled,
  });
  const previewStatus: PreviewStatus = enabled ? _previewStatus : "idle";

  return { previewData, previewStatus };
};
